import { useTranslation } from 'react-i18next';
import { Input } from '../../../ui/Input/index.js';
import { Badge } from '../../../ui/Badge/index.js';
import type { FilterCondition, FilterType } from '../../../../lib/data-viewer-filter.js';
import type { FilterableColumn } from '../../../../types/api.js';

export interface FilterPanelProps {
  /** 登録済みのフィルタ可能カラム定義（カラム名 + データ型のペア。クエリ登録画面で事前定義） */
  filterableColumns: FilterableColumn[];
  /** 現在のフィルタ条件（親が保持する） */
  conditions: FilterCondition[];
  /** 条件が変わったときに親へ通知するハンドラ */
  onChange: (conditions: FilterCondition[]) => void;
}

/** フィルタ型ごとの表示ラベル i18n キー。 @graph-connects none */
const TYPE_LABEL_KEYS: Record<FilterType, string> = {
  date: 'dataViewer.filter.typeDate',
  number: 'dataViewer.filter.typeNumber',
  string: 'dataViewer.filter.typeString',
};

/**
 * 登録済みの filterableColumns から初期フィルタ条件を組み立てる。
 * 各カラムにつき1条件（値は未入力）を生成する。
 * カラム名は id として使うため、重複がある場合は最初の1件のみ採用する
 * （登録画面側でのバリデーションは行っていないため、ここで防御する）。
 *
 * @graph-connects none
 */
export function buildInitialConditions(filterableColumns: FilterableColumn[]): FilterCondition[] {
  const seenIds = new Set<string>();
  const conditions: FilterCondition[] = [];
  for (const filterableColumn of filterableColumns) {
    // DB 内カラムは同名カラムが別テーブルにありうるので、テーブル名を含めて一意にする
    const id =
      filterableColumn.table === undefined
        ? filterableColumn.column
        : `${filterableColumn.table}.${filterableColumn.column}`;
    if (seenIds.has(id)) continue;
    seenIds.add(id);
    conditions.push({
      id,
      column: filterableColumn.column,
      type: filterableColumn.type,
      ...(filterableColumn.table === undefined ? {} : { table: filterableColumn.table }),
    });
  }
  return conditions;
}

/**
 * data-viewer の実行前フィルタ設定パネル。
 *
 * ここで指定した条件は SQL の `WHERE` 句として API 側で適用される。実行結果に対する
 * 絞り込みではないため、表示上限（200 行）の外にある行も対象になる。
 *
 * 全ての型で「値を列挙」（カンマ区切り、`IN` 句になる）を受け付け、date / number は
 * 加えて範囲も指定できる。実行前は選択肢を作るためのデータが無いので、以前の
 * string 型の distinct 値トグルは廃止して自由入力に統一した。
 *
 * 登録時に DB 内カラムから選ばれた条件は `table` を持ち、`table.column` の形で表示する。
 * この条件は集約前の生データに効くため、`SELECT` に出ていないカラムでも絞り込める。
 *
 * 条件の確定は実行ボタンが担うため、このパネルに「反映」ボタンは持たせない（親が
 * conditions を保持する制御コンポーネント）。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 登録済み filterableColumns に基づく data-viewer の実行前フィルタ入力UIを提供する
 * @graph-connects none
 */
export function FilterPanel({ filterableColumns, conditions, onChange }: FilterPanelProps) {
  const { t } = useTranslation();

  const updateCondition = (id: string, patch: Partial<FilterCondition>): void => {
    onChange(conditions.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  if (filterableColumns.length === 0) return null;

  return (
    <div className="glass-card p-4 sm:p-6 rounded-xl space-y-4 animate-content-reveal">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('dataViewer.filter.title')}
        </h2>
        <p className="text-xs text-[var(--color-text-muted)]">{t('dataViewer.filter.help')}</p>
      </div>

      <div className="space-y-3">
        {conditions.map((condition) => (
          <div
            key={condition.id}
            className="rounded-lg border border-[var(--color-border)] p-3 space-y-3"
          >
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                {condition.table === undefined
                  ? condition.column
                  : `${condition.table}.${condition.column}`}
              </p>
              <Badge variant="default" size="sm">
                {t(TYPE_LABEL_KEYS[condition.type])}
              </Badge>
            </div>

            <Input
              type="text"
              label={t('dataViewer.filter.values')}
              placeholder={t('dataViewer.filter.valuesPlaceholder')}
              helperText={t('dataViewer.filter.valuesHelp')}
              value={condition.values ?? ''}
              onChange={(e) => {
                updateCondition(condition.id, { values: e.target.value });
              }}
              fullWidth
            />

            {condition.type === 'date' && (
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Input
                    type="date"
                    label={t('dataViewer.filter.dateFrom')}
                    value={condition.dateFrom ?? ''}
                    onChange={(e) => {
                      updateCondition(condition.id, { dateFrom: e.target.value });
                    }}
                    fullWidth
                  />
                </div>
                <div className="flex-1">
                  <Input
                    type="date"
                    label={t('dataViewer.filter.dateTo')}
                    value={condition.dateTo ?? ''}
                    onChange={(e) => {
                      updateCondition(condition.id, { dateTo: e.target.value });
                    }}
                    fullWidth
                  />
                </div>
              </div>
            )}

            {condition.type === 'number' && (
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Input
                    type="number"
                    label={t('dataViewer.filter.numberMin')}
                    value={condition.numberMin ?? ''}
                    onChange={(e) => {
                      updateCondition(condition.id, { numberMin: e.target.value });
                    }}
                    fullWidth
                  />
                </div>
                <div className="flex-1">
                  <Input
                    type="number"
                    label={t('dataViewer.filter.numberMax')}
                    value={condition.numberMax ?? ''}
                    onChange={(e) => {
                      updateCondition(condition.id, { numberMax: e.target.value });
                    }}
                    fullWidth
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
